import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import cogoToast from 'cogo-toast';
import Select from 'react-select';
import _ from 'lodash';
import './ProductMatchView.scss';

import { updateVariant } from '../../../api/products';
import { fetchVariantsFilterOptions } from '../../../api/queues';

import ProductMatchTicket from './ProductMatchTicket';

class ProductMatchView extends Component {
  static propTypes = {
    queue: PropTypes.object.isRequired,
    syncVariantsQueue: PropTypes.func.isRequired,
    selectTicket: PropTypes.func.isRequired,
    searchVal: PropTypes.string.isRequired,
    updateSearchVal: PropTypes.func.isRequired,
    updateSearchDomainVal: PropTypes.func.isRequired,
    updateSearchBrandVal: PropTypes.func.isRequired,
    updateSearchRetailerVal: PropTypes.func.isRequired,
    queueSearchVal: PropTypes.string.isRequired,
    queueSearchDomainVal: PropTypes.string.isRequired,
    queueSearchBrandVal: PropTypes.string.isRequired,
    queueSearchRetailerVal: PropTypes.string.isRequired,
    basicUnmatchedOnly: PropTypes.bool,
    adminUnmatchedOnly: PropTypes.bool,
    performSearch: PropTypes.func,
    selectedTicket: PropTypes.object
  };

  componentDidMount() {
    this.searchTicketsDebouncer = AwesomeDebouncePromise(() => {
      this.setState({ isSearching: true });
      this.props.syncVariantsQueue().then(() => {
        this.setState({ isSearching: false });
      });
    }, 250);

    document.addEventListener('mouseup', this.handleHighlightMouseUp);
  }

  componentWillUnmount() {
    document.removeEventListener('mouseup', this.handleHighlightMouseUp);
  }

  handleHighlightMouseUp = e => {
    const isChildOfHighlightZone = e.target && !!e.target.closest('.highlight-zone');
    const selection = window.getSelection().toString().trim();
    const isSearchableSelection = selection.length >= 3 || /^\d+$/.test(selection);
    if (selection && isSearchableSelection && isChildOfHighlightZone) {
      this.props.performSearch(selection);
    }
  };

  state = {
    isSearching: false,
    unmatchableTicketIds: [], // To speed up deletion, let's hide anything that is in the process of being removed
    facetInputs: { domain: '', rawBrand: '', retailer: '' },
    facetOptions: { domain: [], rawBrand: [], retailer: [] },
    facetLoading: { domain: false, rawBrand: false, retailer: false }
  };

  setFacetState = (field, { options, loading }) =>
    this.setState(state => {
      const next = {};
      if (options !== undefined) next.facetOptions = { ...state.facetOptions, [field]: options };
      if (loading !== undefined) next.facetLoading = { ...state.facetLoading, [field]: loading };
      return next;
    });

  facetAbort = { domain: null, rawBrand: null, retailer: null };

  // A debounced, cancellable options fetch for one facet field
  makeFacetLoader = field => {
    let latestReqId = 0;
    return AwesomeDebouncePromise(async inputValue => {
      this.facetAbort[field]?.abort();
      const reqId = ++latestReqId;
      const controller = new AbortController();
      this.facetAbort[field] = controller;
      this.setFacetState(field, { loading: true });
      try {
        const options = await fetchVariantsFilterOptions(field, inputValue, {
          basicUnmatchedOnly: this.props.basicUnmatchedOnly,
          adminUnmatchedOnly: this.props.adminUnmatchedOnly,
          signal: controller.signal
        });
        if (reqId === latestReqId) this.setFacetState(field, { options, loading: false });
      } catch {
        if (reqId === latestReqId) this.setFacetState(field, { options: [], loading: false });
      }
    }, 500);
  };

  loadDomainOptions = this.makeFacetLoader('domain');
  loadBrandOptions = this.makeFacetLoader('rawBrand');
  loadRetailerOptions = this.makeFacetLoader('retailer');

  markAsUnmatchable = ticket => {
    this.setState({ unmatchableTicketIds: [...this.state.unmatchableTicketIds, ticket.id] });
    updateVariant(ticket.id, { requiresAdminAttention: true, adminMatchNotes: 'Could not find product.' }).then(
      () => {
        this.props.syncVariantsQueue();
        this.setState({ unmatchableTicketsMap: this.state.unmatchableTicketIds.filter(id => id !== ticket.id) });
      },
      () => {
        cogoToast.error('There was an issue orphaning this variant.');
      }
    );
  };

  changeSearch = newVal => {
    this.props.updateSearchVal(newVal);
    this.searchTicketsDebouncer();
  };

  changeSearchDomain = newVal => {
    this.props.updateSearchDomainVal(newVal);
    this.searchTicketsDebouncer();
  };

  changeSearchBrand = newVal => {
    this.props.updateSearchBrandVal(newVal);
    this.searchTicketsDebouncer();
  };

  changeSearchRetailer = newVal => {
    this.props.updateSearchRetailerVal(newVal);
    this.searchTicketsDebouncer();
  };

  // The selected chip(s) come from the caller's comma-joined local state, so they render without a
  // preloaded option list. label === value since the facet value is the display text.
  toSelectValue = csv => _.filter((csv || '').split(',')).map(val => ({ label: val, value: val }));

  noOptionsMessage = ({ inputValue }) => (inputValue && inputValue.length >= 2 ? 'No matches' : 'Type to search');

  clearFacet = field => {
    this.facetAbort[field]?.abort();
    this.setFacetState(field, { options: [], loading: false });
  };

  // Typing >= 2 chars refetches; emptying the box (or dropping below 2 chars) clears the options
  // Blur and menu-close are ignored so clicking away preserves the text and options.
  handleFacetInput = (field, loadOptions) => (value, { action }) => {
    if (action === 'input-change') {
      this.setState(state => ({ facetInputs: { ...state.facetInputs, [field]: value } }));
      if ((value || '').length < 2) this.clearFacet(field);
      else loadOptions(value);
    } else if (action === 'set-value') {
      this.setState(state => ({ facetInputs: { ...state.facetInputs, [field]: '' } }));
      this.clearFacet(field);
    }
  };

  renderFacetSelect = ({ field, placeholder, loadOptions, selectedCsv, onChange }) => (
    <Select
      isMulti
      isClearable
      className='select'
      placeholder={placeholder}
      options={this.state.facetOptions[field]}
      isLoading={this.state.facetLoading[field]}
      inputValue={this.state.facetInputs[field]}
      onInputChange={this.handleFacetInput(field, loadOptions)}
      filterOption={null}
      noOptionsMessage={this.noOptionsMessage}
      onChange={onChange}
      value={this.toSelectValue(selectedCsv)}
    />
  );

  render() {
    const { queue, selectedTicket, searchVal, performSearch, selectTicket } = this.props;
    const { isSearching, unmatchableTicketIds } = this.state;
    const { variantTickets } = queue;
    const visibleVariantTickets = variantTickets?.filter(ticket => !unmatchableTicketIds.includes(ticket.id));

    return (
      <div className='product-match-view-container panel'>
        <input
          placeholder='Search Match Tickets'
          className='search-input'
          value={searchVal}
          onChange={({ target }) => this.changeSearch(target.value)}
        />
        {this.renderFacetSelect({
          field: 'domain',
          placeholder: 'Website Domain',
          loadOptions: this.loadDomainOptions,
          selectedCsv: this.props.queueSearchDomainVal,
          onChange: resp => this.changeSearchDomain(_.map(resp, 'value').join(','))
        })}
        {this.renderFacetSelect({
          field: 'retailer',
          placeholder: 'Retailer',
          loadOptions: this.loadRetailerOptions,
          selectedCsv: this.props.queueSearchRetailerVal,
          onChange: resp => this.changeSearchRetailer(_.map(resp, 'value').join(','))
        })}
        {this.renderFacetSelect({
          field: 'rawBrand',
          placeholder: 'Brand Name',
          loadOptions: this.loadBrandOptions,
          selectedCsv: this.props.queueSearchBrandVal,
          onChange: resp => this.changeSearchBrand(_.map(resp, 'value').join(','))
        })}
        <div className='match-items-list'>
          {visibleVariantTickets && visibleVariantTickets.length
            ? visibleVariantTickets.map(ticket => (
                <ProductMatchTicket
                  ticket={ticket}
                  key={ticket.id}
                  performSearch={performSearch}
                  isSelected={!!selectedTicket && ticket.id === selectedTicket.id}
                  isVariant
                  markAsUnmatchable={this.markAsUnmatchable}
                  selectTicket={() => selectTicket(ticket)}
                />
              ))
            : !isSearching && <div className='empty-msg'>No results</div>}
        </div>
      </div>
    );
  }
}

export default ProductMatchView;
